import { ChoiceGroup, DefaultButton, Dialog, DialogContent, DialogFooter, DialogType, IChoiceGroupOption, IDialogContentProps, IModalProps, ISpinnerProps, mergeStyles, Panel, PanelType, PrimaryButton, Spinner, SpinnerSize, TextField } from "@fluentui/react";
import React from "react";
import { useSelector } from "react-redux";
import { useLazyBulkValidateUpdateListItemsQuery } from "../../src/taskpane/components/_v2/redux/api/sharepoint/api-sharepoint-slice";
import { RootState } from "../../src/taskpane/components/_v2/redux/storeMain";
import authModule from "../../utilities/AuthModule";
import { LanguageContext } from "../../utilities/LocalizationModule";
import { logger } from "../../utilities/logger";
import { CMErrorModal } from "./CMErrorModal";

const dialogContentProps: IDialogContentProps = {
    type: DialogType.normal,
    showCloseButton: true,
    closeButtonAriaLabel: 'Close',
};

export interface ICMVersionAppRejModalProps {
    selectedItems: any[];
    showDialog: boolean;
    isInOneDrive: boolean;
    closeDialog: () => void;
    closeOnSuccess: () => void;
}

export const CMVersionAppRejModal = ({ selectedItems, showDialog, isInOneDrive, closeDialog, closeOnSuccess }: ICMVersionAppRejModalProps) => {
    const languageStrings = React.useContext(LanguageContext);

    const currentSharepointFolder = useSelector((state: RootState) => state.currentSharepointFolder);
    const currentDirectory = useSelector((state: RootState) => state.workingDirectories.workingDirectories.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null);
    const oneDriveRootDetails = useSelector((state: RootState) => state.workingDirectories.oneDriveRootDetails);

    const [isCMErrorModalOpen, setIsCMErrorModalOpen] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState('');

    const [comment, setComment] = React.useState('');
    const [checkinError, setCheckinError] = React.useState('');
    const [isBusy, setIsBusy] = React.useState(false);

    const [bulkValidateUpdateListItems] = useLazyBulkValidateUpdateListItemsQuery();

    const [selectedOption, setSelectedOption] = React.useState<IChoiceGroupOption>()
    const [options, setOptions] = React.useState<IChoiceGroupOption[]>([
        { key: '0', text: languageStrings.VersionStatusApproved },
        { key: '1', text: languageStrings.VersionStatusRejected },
        { key: '2', text: languageStrings.VersionStatusPending },
    ])

    React.useEffect(() => {
        if (selectedItems && selectedItems.length === 1 && options.length > 0) {
            setSelectedOption(options.find(x => x.key === selectedItems[0]['_ModerationStatus.']))
        } else {
            setSelectedOption(options[2])
        }
    }, [selectedItems, options]);

    const onConfirm = async () => {
        setIsBusy(true);
        setCheckinError('');

        try {
            await Promise.all(selectedItems.map(async item => {
                try {
                    const results = await bulkValidateUpdateListItems({
                        siteUrl: isInOneDrive ? oneDriveRootDetails?.sharePointSiteUrl : currentDirectory?.webUrl,
                        accessToken: isInOneDrive ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
                        sharePointListId: currentSharepointFolder.sharepointListId,
                        comments: comment,
                        status: selectedOption.key,
                        itemId: item.ID
                    }).unwrap();

                    if (results.value[0]?.ErrorMessage?.length > 0) {
                        setErrorMessage(results.value[0]?.ErrorMessage);
                        setIsCMErrorModalOpen(true);
                    }

                } catch (error) {
                    logger(error, "CMCheckinModal.tsx Promise.all (onConfirmCheckin - publish)");
                }
            }));

            setIsBusy(false);
            closeOnSuccess();
        } catch (error) {
            logger(error, "CMCheckinModal.tsx (onConfirmCheckin - publish)");
            setCheckinError(error?.data?.['odata.error']?.message?.value);
            setIsBusy(false);
        }
    }

    const onCommentChanged = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        setComment(newValue);
    }

    function _onChange(ev: React.FormEvent<HTMLInputElement>, option: IChoiceGroupOption): void {
        setSelectedOption(option);
    }

    return (
        <>
            <Dialog hidden={!showDialog} onDismiss={() => closeDialog()} type={DialogType.normal} title={`${languageStrings.Approve}/${languageStrings.Reject}`} dialogContentProps={dialogContentProps} minWidth={600} maxWidth={600}>
                <DialogContent styles={{ inner: { padding: 0 }, header: { display: 'none' } }}>
                    {selectedOption && <ChoiceGroup defaultSelectedKey={selectedOption.key} options={options} onChange={_onChange} label={`${languageStrings.Approve} ${languageStrings.Status.toLocaleLowerCase()}`} required={true} />}
                    <div>
                        <TextField autoFocus label={languageStrings.Comment} multiline rows={3} value={comment} onChange={onCommentChanged} errorMessage={checkinError} />
                    </div>
                </DialogContent>
                <DialogFooter>
                    <DefaultButton onClick={() => closeDialog()} text={languageStrings.Cancel} disabled={isBusy} />
                    <PrimaryButton onClick={() => onConfirm()} text="Ok" disabled={isBusy}>
                        {isBusy && <Spinner size={SpinnerSize.small} style={{ paddingLeft: '5px' }} />}
                    </PrimaryButton>
                </DialogFooter>
            </Dialog>
            {isCMErrorModalOpen && <CMErrorModal message={errorMessage} showDialog={isCMErrorModalOpen} closeDialog={() => setIsCMErrorModalOpen(false)} />}
        </>
    )
};